import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { toast } from 'react-toastify';
import Modal from '../../_common/Modal/Modal';
import classes from './reports.module.scss';
import { ReactComponent as DotesIcon } from '../../../assets/icons/dotes.svg';
import { ReactComponent as Eyecon } from '../../../assets/icons/eye.svg';
import Button from '../../_common/Form/Buttons/Button';
import AccountHelpers from '../../../utils/accountHelpers';
import { getAccountRequest } from '../../../store/actions/auth';
import Utils from '../../../utils/utils';

const manufacturerReportColumns = [
  'Physician',
  'Patient',
  'Medication',
  'Location',
  'Quantity',
  'Dosage',
  'Authorization #',
  'Confirmation Date',
  'Confirmation Number',
  'Counselor',
  'Checklist',
  'Counsel Date',
  'Dispense Date',
  'Ship Date',
  'Delivery Method',
  'New Start',
  'ADE',
  'Med Guide',
  'Risk Category',
];

const ManufacturerReportModal = ({ modalInfo, filters, onClose }) => {
  const dispatch = useDispatch();
  const columns = useSelector((store) => store.auth.account?.columns || []);

  const [items, setItems] = useState([]);

  const [activeItem, setActiveItem] = useState(null);

  const [saveLoading, saveLoadingToggle] = useState(false);

  useEffect(() => {
    if (modalInfo.isOpen) {
      setItems(columns.length
        ? [...columns, ...manufacturerReportColumns.filter((c) => !columns.includes(c))].map((c, i) => ({
          id: i.toString(),
          title: c,
          visible: columns.includes(c),
        }))

        : manufacturerReportColumns.map((c, i) => ({
          id: i.toString(),
          title: c,
          visible: true,
        })));
    }
  }, [modalInfo.isOpen]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const onDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const onDragStart = ({ active }) => {
    const foundItem = items.find((it) => it.id === active.id);

    setActiveItem(foundItem);
  };
  const toggleVisibility = (id) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, visible: !item.visible } : item)));
  };

  const onSave = async () => {
    const selectedItems = items.filter((i) => i.visible).map((i) => i.title);

    if (!selectedItems.length) {
      toast.error('Please select at least one column');
    } else {
      saveLoadingToggle(true);

      const sendingData = Utils.deleteEmptyKeys(filters);
      delete sendingData.page;
      delete sendingData.limit;

      const { data } = await axios(modalInfo.link, {
        params: {
          download: true,
          columns: selectedItems.length ? selectedItems.join(',') : null,
          ...sendingData,
        },
        headers: {
          Authorization: `Bearer ${AccountHelpers.getToken()}`,
        },
        responseType: 'blob',
      });

      fileDownload(data, 'manufacturer.xlsx');

      await dispatch(getAccountRequest(AccountHelpers.getUser().id));

      onClose();
      saveLoadingToggle(false);
    }
  };

  return (
    <Modal
      open={modalInfo.isOpen}
      onClose={onClose}
      className={classes.report_modal}
    >
      <h2>Download Report</h2>

      <div className={classes.subtitle}>
        <p>Reorder columns by dragging them and choose which ones to include.</p>

        <p>Your download will match this setup.</p>
      </div>

      <div className={classes.list_wrapper}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={() => setActiveItem(null)}
        >
          <SortableContext
            items={items.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((item) => (
              <SortableItem
                key={item.id}
                item={item}
                toggleVisibility={toggleVisibility}
              />
            ))}
          </SortableContext>

          {activeItem && (
            <DragOverlay dropAnimation={null}>
              <div>
                <SortableItem
                  item={activeItem}
                  draggable
                />
              </div>
            </DragOverlay>
          )}
        </DndContext>
      </div>

      <div className={classes.btns_wrapper}>
        <Button
          title="Cancel"
          type="button"
          onClick={onClose}
          clearButton
        />

        <Button
          title="Download"
          type="button"
          onClick={onSave}
          loading={saveLoading}
        />
      </div>
    </Modal>
  );
};

function SortableItem({ item, toggleVisibility, draggable }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classes.item_wrapper}
    >
      <div
        style={{ opacity: item.visible ? 1 : 0.3 }}
        className={classes.title}
      >
        <div
          style={{ cursor: draggable ? 'grabbing' : 'grab' }}
          className={classes.dotes_icon}
          {...attributes}
          {...listeners}
        >
          <DotesIcon />
          <DotesIcon />
        </div>

        {item.title}
      </div>

      <button
        onClick={() => toggleVisibility(item.id)}
      >
        <Eyecon className={item.visible ? classes.visible : ''} />
      </button>
    </div>
  );
}

ManufacturerReportModal.propTypes = {

};

export default ManufacturerReportModal;
